import { Box, CircularProgress } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router';
import { getOktaForgotPasswordUrl, getOktaSession, logOutOfOkta } from '../../utils';
import { LoginForm } from './LoginForm';
/**
 * Okta login form component that determines login session status
 * and renders either a `LoginForm` or loading spinner accordingly.
 *
 * @param {OktaLoginFormProps} root0 - Component props.
 * @param {string} root0.oktaUrl - URL of the Okta tenant to check session or initiate logout.
 * @param {(navigate: NavigateFunction, loginEmail?: string) => void} root0.onLogin - Callback triggered on login continue or login as other.
 * @param {(() => void) | undefined} root0.onRegister - Optional callback for the registration flow.
 * @param {React.ReactNode} [root0.footerElement] - Optional custom React node to render in the login form footer.
 * @param {string} [root0.footerHtml] - Optional raw HTML string to render in the login form footer.
 * @returns {JSX.Element} A rendered Okta session-aware login form or loading spinner.
 */
export const OktaLoginForm = ({ oktaUrl, onLogin, onRegister, footerElement, footerHtml }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = React.useState(true);
    const [session, setSession] = React.useState();
    React.useEffect(() => {
        setLoading(true);
        setSession(undefined);
        getOktaSession(oktaUrl)
            .then((response) => {
            setLoading(false);
            setSession(response);
        })
            .catch(() => {
            setLoading(false);
            setSession(undefined);
        });
    }, [oktaUrl]);
    const handleLoginContinue = () => {
        onLogin(navigate, session === null || session === void 0 ? void 0 : session.login);
    };
    const [disabled, setDisabled] = React.useState(false);
    const handleLoginOther = () => {
        setDisabled(true);
        logOutOfOkta(oktaUrl).finally(() => {
            setDisabled(false);
            onLogin(navigate);
        });
    };
    if (loading) {
        return (React.createElement(Box, null,
            React.createElement(CircularProgress, null)));
    }
    else {
        return (React.createElement(LoginForm, { disabled: disabled, loginEmail: session === null || session === void 0 ? void 0 : session.login, onLoginContinue: handleLoginContinue, onLoginOther: handleLoginOther, onLogin: onLogin, forgotPasswordHref: getOktaForgotPasswordUrl(oktaUrl), onRegister: onRegister, footerElement: footerElement, footerHtml: footerHtml }));
    }
};
